import React, { useEffect, useMemo, useState } from "react";
import { Editdoc, Viewdoc, Deleteicon, Changesign } from "../../Icon";
import { useNavigate } from "react-router-dom";
import { FaPrint } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify"
import { API_BASE_URL } from "../../utils/CONSTANTS";
import Header from "../../components/header";
import { canEditFaiPdi } from "../../utils/roles";

const Fdimenu = () => {
    const navigate = useNavigate();
    const [complaints, setComplaints] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedComplaint, setSelectedComplaint] = useState(null);
    const [showPdiModal, setShowPdiModal] = useState(false);
    const [pdiNumber, setPdiNumber] = useState("");
    const [pdiNumbers, setPdiNumbers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const canEdit = canEditFaiPdi();

    // Fetch FDI / FAI data
    const getfdidata = async () => {
        try {
            const mode = "fdi"; // original code used "fdi"
            const response = await axios.get(`${API_BASE_URL}/api/product/getfdi/${mode}`);
            const result = response.data;
            if (Array.isArray(result.isdata)) {
                const allFdi = result.isdata.map(item => {
                    const id = item._id || item.id || `${Math.random()}`;
                    const customerName = (item.partName ?? item.customerName ?? "") + "";
                    // prefer explicit reportno, otherwise productName
                    const reportno = (item.reportno ?? item.productName ?? "") + "";
                    const cardno = (item.cardno ?? "") + "";
                    const drgno = (item.drgno ?? "") + "";
                    const modeVal = (item.mode ?? "") + "";
                    const pdino = (item.pdino ?? "") + "";

                    return {
                        id,
                        customerName,
                        reportno,
                        cardno,
                        drgno,
                        mode: modeVal,
                        pdino
                    };
                });
                setComplaints(allFdi);
            } else {
                setComplaints([]);
            }
        } catch (err) {
            console.error("Error fetching FDI data:", err?.message ?? err);
            setComplaints([]);
        }
    };

    // Fetch existing PDI numbers (to prevent duplicates)
    const getAllPdiNumbers = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/product/getpdino`);
            if (response.status === 200 && Array.isArray(response.data.allPdiNumbers)) {
                setPdiNumbers(response.data.allPdiNumbers.map(item => (item.pdino ?? "") + ""));
            } else {
                setPdiNumbers([]);
            }
        } catch (error) {
            console.error("Error fetching PDI numbers:", error?.message ?? error);
            setPdiNumbers([]);
        }
    };

    useEffect(() => {
        getfdidata();
        getAllPdiNumbers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Delete by id (safer)
    const handleDelete = async () => {
        if (!selectedComplaint) return;
        try {
            const idToDelete = selectedComplaint.id || selectedComplaint;
            const response = await axios.delete(`${API_BASE_URL}/api/product/deleteproduct/${idToDelete}`);
            if (response.status === 200 || response.status === 204) {
                setComplaints(prev => prev.filter(item => item.id !== idToDelete));
                setShowModal(false);
                setSelectedComplaint(null);
                toast.success("Record deleted");
            } else {
                toast.error("Failed to delete record");
            }
        } catch (err) {
            console.log("Error deleting:", err?.message ?? err);
            toast.error("Error deleting record");
        }
    };

    // Convert selected item to PDI (assign pdino)
    const handleConvertToFDI = async () => {
        if (!selectedComplaint || !pdiNumber) {
            toast.error("Select an item and enter PDI number");
            return;
        }

        if (pdiNumbers.includes(String(pdiNumber))) {
            toast.error("PDI number already exists!");
            return;
        }

        try {
            await axios.put(`${API_BASE_URL}/api/product/updatemode`, {
                reportno: selectedComplaint.reportno,
                newMode: "pdi",
                pdino: pdiNumber
            });

            // update local state: set mode to 'pdi' and pdino
            setComplaints(prev => prev.map(item => item.id === selectedComplaint.id ? { ...item, mode: "pdi", pdino: pdiNumber } : item));

            // add to pdiNumbers list to prevent duplicates immediately
            setPdiNumbers(prev => [...prev, String(pdiNumber)]);

            toast.success("Converted to PDI successfully");
            setShowPdiModal(false);
            setPdiNumber("");
            setSelectedComplaint(null);
        } catch (err) {
            console.log("Error updating mode:", err?.message ?? err);
            toast.error("Error converting to PDI");
        }
    };

    // Derived filtered list based on searchTerm (defensive), sorted by Report No.
    const filteredComplaints = useMemo(() => {
        const filtered = complaints.filter(item => {
        if (!searchTerm || searchTerm.trim() === "") return true;
        const q = searchTerm.trim().toLowerCase();

        const cardnoStr = (item.cardno !== undefined && item.cardno !== null) ? String(item.cardno).toLowerCase() : "";
        const custName = (item.customerName !== undefined && item.customerName !== null) ? String(item.customerName).toLowerCase() : "";
        const reportNo = (item.reportno !== undefined && item.reportno !== null) ? String(item.reportno).toLowerCase() : "";

        return cardnoStr.includes(q) || custName.includes(q) || reportNo.includes(q);
        });

        return [...filtered].sort((a, b) => {
            const aNum = Number(a.reportno);
            const bNum = Number(b.reportno);
            if (!isNaN(aNum) && !isNaN(bNum)) return bNum - aNum;
            return String(b.reportno ?? "").localeCompare(String(a.reportno ?? ""), undefined, { numeric: true });
        });
    }, [complaints, searchTerm]);

    return (
        <>
            <Header />
            <div className="p-6 bg-gray-100 min-h-screen">
                <div className="max-w-6xl mx-auto bg-white shadow-md p-6 rounded-lg">
                    <h1 className="text-2xl font-semibold mb-4">FAI Menu</h1>

                    <div className="mb-4 flex gap-2">
                        <input
                            type="text"
                            placeholder="Search by Card No, Consumer Name or Report No"
                            className="flex-1 p-2 border border-gray-300 rounded-md"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button
                            className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                            onClick={() => setSearchTerm("")}
                        >
                            Clear
                        </button>
                    </div>

                    <div className="flex justify-end mb-4">
                        <button className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800" onClick={() => navigate("/fdiform", { state: { mode: "add" } })}>New FAI Form</button>
                    </div>

                    <table className="w-full border-collapse border border-gray-300">
                        <thead>
                            <tr className="bg-gray-200">
                                <th className="border border-gray-300 px-4 py-2">Sr No.</th>
                                <th className="border border-gray-300 px-4 py-2">Report No.</th>
                                <th className="border border-gray-300 px-4 py-2">Card No.</th>
                                <th className="border border-gray-300 px-4 py-2">Consumer Name</th>
                                <th className="border border-gray-300 px-4 py-2">Drg No.</th>
                                <th className="border border-gray-300 px-4 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredComplaints.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center p-4 text-gray-500">No records found.</td>
                                </tr>
                            ) : (
                                filteredComplaints.map((complaint, index) => (
                                    <tr
                                        key={complaint.id}
                                        className="text-center"
                                        style={complaint.mode === "pdi" ? { backgroundColor: "#E1EEBC" } : {}}
                                    >
                                        <td className="border border-gray-300 px-4 py-2">{index + 1}</td>
                                        <td className="border border-gray-300 px-4 py-2">{complaint.reportno}</td>
                                        <td className="border border-gray-300 px-4 py-2">{complaint.cardno}</td>
                                        <td className="border border-gray-300 px-4 py-2">{complaint.customerName}</td>
                                        <td className="border border-gray-300 px-4 py-2">{complaint.drgno}</td>
                                        <td className="flex justify-around border border-gray-300 px-4 py-2">
                                            <button className="flex flex-col items-center px-3 py-1 rounded-md hover:bg-slate-300" onClick={() => navigate("/fdipdf", { state: { name: complaint.customerName } })}>
                                                <Viewdoc />
                                                <div>View</div>
                                            </button>
                                            {canEdit && (
                                              <>
                                            <button className="flex flex-col items-center px-3 py-1 rounded-md hover:bg-slate-300" onClick={() => navigate("/fdiform", { state: { mode: "update", name: complaint.customerName } })}>
                                                <Editdoc />
                                                <div>Edit</div>
                                            </button>
                                            <button className="flex flex-col items-center px-3 py-1 rounded-md hover:bg-slate-300" onClick={() => { setSelectedComplaint(complaint); setShowModal(true); }}>
                                                <Deleteicon />
                                                <div>Delete</div>
                                            </button>

                                            <button
                                                className="flex flex-col items-center px-3 py-1 rounded-md hover:bg-slate-300"
                                                onClick={() => {
                                                    setSelectedComplaint(complaint);
                                                    setShowPdiModal(true);
                                                }}
                                            >
                                                <Changesign />
                                                <div>Convert to PDI</div>
                                            </button>
                                              </>
                                            )}

                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {showPdiModal && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white p-6 rounded-md shadow-md">
                            <h2 className="text-xl font-semibold mb-4">Enter PDI Number</h2>
                            <input
                                type="text"
                                value={pdiNumber}
                                onChange={(e) => setPdiNumber(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-md mb-4"
                                placeholder="Enter PDI number"
                            />
                            <div className="flex justify-end mt-4">
                                <button className="bg-gray-300 px-4 py-2 rounded-md mr-2" onClick={() => { setShowPdiModal(false); setPdiNumber(""); }}>Cancel</button>
                                <button className="bg-blue-500 text-white px-4 py-2 rounded-md" onClick={handleConvertToFDI}>Convert</button>
                            </div>
                        </div>
                    </div>
                )}

                {showModal && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white p-6 rounded-md shadow-md">
                            <h2 className="text-xl font-semibold mb-4">Confirm Deletion</h2>
                            <p>Are you sure you want to delete {selectedComplaint?.customerName || selectedComplaint?.id || selectedComplaint}?</p>
                            <div className="flex justify-end mt-4">
                                <button className="bg-gray-300 px-4 py-2 rounded-md mr-2" onClick={() => setShowModal(false)}>Cancel</button>
                                <button className="bg-red-500 text-white px-4 py-2 rounded-md" onClick={handleDelete}>Delete</button>
                            </div>
                        </div>
                    </div>
                )}
            </div >
        </>
    );
};

export default Fdimenu;
